/*
  <pdp-sticky-atc> — shows the sticky bar once the page is scrolled past a threshold.

  This is the entire behaviour, and it is one attribute: the element sets `data-shown` on the bar
  and CSS does the rest. Deliberately small, because the Replo original made the bar's very
  EXISTENCE depend on its runtime — `display: none` until the bundle loaded and decided otherwise,
  which is why the converted sections either showed it permanently or never.

  Here the bar is in the DOM and styled from the start; if this file fails to load the bar simply
  stays hidden, which is the safe direction.

  Attributes:
    threshold  scrollY in px past which the bar appears (default 1000, measured on live)
*/

class PdpStickyAtc extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-pdp-sticky-bar]');
    if (!this.bar) return;

    this.threshold = Number(this.getAttribute('threshold')) || 1000;

    // Passive + rAF-coalesced: this runs on every scroll frame on a very long page, and the work
    // must never be able to make scrolling janky.
    let queued = false;
    this.onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; this.sync(); });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });

    /*
      "Update" sends the customer to the theme's own variant picker rather than duplicating it.
      The Replo original bound this to its runtime; this is the honest way to reach the one real
      picker on the page.

      There is not one picker class, there are two. The buy box renders `.pdp-redesign__variants`
      on AIR and Compression, but `.pdp-variant-picker` on Compression PRO, TENS Pod and TENS Pod
      PRO. The section setting defaults to `.pdp-redesign__variants` and that value is written onto
      every page, so on the three `.pdp-variant-picker` pages querySelector returned null, the
      handler hit `if (!target) return`, and the button did nothing at all — measured as scrollY
      unchanged at 2600 before and after the click, while AIR and Compression scrolled correctly.

      So try the configured selector first, then both known pickers, then the product form as a
      last resort — and require the candidate to be VISIBLE and to sit outside this bar. Visible,
      because the buy box can render a hidden duplicate for another breakpoint and scrolling to a
      zero-height node lands nowhere. Outside the bar, because the bar contains its own
      /cart/add form and must never scroll to itself.
    */
    this.querySelector('[data-pdp-sticky-update]')?.addEventListener('click', (e) => {
      const target = this.updateTarget();
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.querySelector('input, select, button')?.focus({ preventScroll: true });
    });

    this.sync();
  }

  updateTarget() {
    const selectors = [
      this.getAttribute('update-target'),
      '.pdp-redesign__variants',
      '.pdp-variant-picker',
      'form[action*="/cart/add"]',
    ].filter(Boolean);

    for (const selector of selectors) {
      for (const el of document.querySelectorAll(selector)) {
        if (this.contains(el)) continue;                       // never this bar's own form
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) return el;            // must actually be rendered
      }
    }
    return null;
  }

  disconnectedCallback() {
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
  }

  /*
    Computers on the wellness buy box (Justyn 17/10/2026, "sticky appears too soon here on desktop"): the
    buy box sits beside the photos and runs down to the How we compare table, so a fixed 1000px showed
    the bar while that table was still on screen. There the bar waits until the whole buy box section
    has scrolled up under the header. Phones and every other page keep the threshold.
  */
  pastBuyBox() {
    if (!window.matchMedia('(min-width: 750px)').matches) return null;
    const box = document.querySelector('.pdp-redesign--well');
    if (!box) return null;
    return box.getBoundingClientRect().bottom < 120;
  }

  sync() {
    const past = this.pastBuyBox();
    const shown = past === null ? window.scrollY > this.threshold : past;
    if (shown === this.shown) return;
    this.shown = shown;
    if (shown) this.bar.setAttribute('data-shown', '');
    else this.bar.removeAttribute('data-shown');
  }
}

if (!customElements.get('pdp-sticky-atc')) {
  customElements.define('pdp-sticky-atc', PdpStickyAtc);
}
